import React from "react";
import { Box, Typography, Chip, Link } from "@mui/material";

const TRAILER_REGEX =
  /\n*\s*The AI Quality Check \(QC\) for this Subscription .*/is;

export function splitReasoningAndTrailer(full: string): {
  main: string;
  trailer: string;
} {
  const m = full.match(TRAILER_REGEX);
  if (!m || m.index === undefined) {
    return { main: full.trim(), trailer: "" };
  }
  return {
    main: full.slice(0, m.index).trim(),
    trailer: full.slice(m.index).trim(),
  };
}

/** Split main reasoning into lines; handle legacy single-paragraph numbered lists. */
export function splitIntoCheckLines(main: string): string[] {
  const t = main.trim();
  if (!t) return [];
  const byNewline = t
    .split(/\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (byNewline.length > 1) return byNewline;
  const single = byNewline[0] ?? "";
  const chunks = single
    .split(/(?=\d+\.\s)/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (chunks.length > 1) return chunks;
  // Inline "1. … 2. …" glued with spaces only (no newlines)
  const spaced = single
    .split(/\s+(?=\d+\.\s)/)
    .map((s) => s.trim())
    .filter(Boolean);
  const numberedCount = spaced.filter((s) => /^\d+\.\s/.test(s)).length;
  if (spaced.length > 1 && numberedCount >= 2) return spaced;
  return [single];
}

type ParsedLine =
  | { kind: "overall"; body: string }
  | {
      kind: "check";
      ordinal?: number;
      label: string;
      status?: "PASS" | "FAIL";
      body: string;
    };

function parseCheckLine(line: string): ParsedLine {
  const trimmed = line.trim();
  const overall = /^Overall:\s*(.+)$/i.exec(trimmed);
  if (overall) {
    return { kind: "overall", body: overall[1].trim() };
  }

  const num = /^(\d+)\.\s+/.exec(trimmed);
  const withoutNum = num ? trimmed.slice(num[0].length).trim() : trimmed;

  const structured = /^(.+?)\s*[—–-]\s*(PASS|FAIL)\s*:\s*(.*)$/i.exec(
    withoutNum
  );
  if (structured) {
    return {
      kind: "check",
      ordinal: num ? parseInt(num[1], 10) : undefined,
      label: structured[1].trim(),
      status: structured[2].toUpperCase() as "PASS" | "FAIL",
      body: structured[3].trim() || structured[1].trim(),
    };
  }

  const looseStatus = /\b(PASS|FAIL)\b/i.exec(withoutNum);
  const status = looseStatus
    ? (looseStatus[1].toUpperCase() as "PASS" | "FAIL")
    : undefined;
  const labelPart = looseStatus
    ? withoutNum.slice(0, looseStatus.index).replace(/[—–:\s]+$/g, "").trim()
    : withoutNum;
  const bodyPart = looseStatus
    ? withoutNum
        .slice(looseStatus.index + looseStatus[0].length)
        .replace(/^[:\s,.]+/, "")
        .trim()
    : withoutNum;

  return {
    kind: "check",
    ordinal: num ? parseInt(num[1], 10) : undefined,
    label: labelPart || "Check",
    status,
    body: bodyPart || labelPart,
  };
}

function trailerContainsUrl(trailer: string): React.ReactNode {
  const urlMatch = trailer.match(/https?:\/\/[^\s]+/);
  if (!urlMatch) return trailer;
  const url = urlMatch[0];
  const idx = trailer.indexOf(url);
  const before = trailer.slice(0, idx);
  const after = trailer.slice(idx + url.length);
  return (
    <>
      {before}
      <Link href={url} target="_blank" rel="noreferrer">
        {url}
      </Link>
      {after}
    </>
  );
}

function isStructuredReasoning(lines: string[]): boolean {
  return lines.some((l) => /^\d+\.\s/.test(l.trim()));
}

const SubscriptionQcReasoning: React.FC<{ reasoning: string }> = ({
  reasoning,
}) => {
  const { main, trailer } = splitReasoningAndTrailer(reasoning);
  const lines = splitIntoCheckLines(main);
  const parsed = lines.map(parseCheckLine);

  const structured = isStructuredReasoning(lines);

  if (!structured && main.length > 0) {
    return (
      <Box>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
          {main}
        </Typography>
        {trailer ? (
          <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
            {trailerContainsUrl(trailer)}
          </Typography>
        ) : null}
      </Box>
    );
  }

  return (
    <Box>
      <Box component="ul" sx={{ m: 0, pl: 0, listStyle: "none" }}>
        {parsed.map((p, i) => {
          if (p.kind === "overall") {
            return (
              <Box
                component="li"
                key={`overall-${i}`}
                sx={{
                  mt: 2,
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  borderLeft: (theme) => `4px solid ${theme.palette.divider}`,
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ textTransform: "uppercase", letterSpacing: 0.06 }}
                >
                  Overall
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {p.body}
                </Typography>
              </Box>
            );
          }

          return (
            <Box
              component="li"
              key={`check-${i}`}
              sx={{
                py: 1.5,
                borderBottom: (theme) =>
                  i < parsed.length - 1
                    ? `1px solid ${theme.palette.divider}`
                    : "none",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  flexWrap: "wrap",
                }}
              >
                {p.ordinal != null ? (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ minWidth: "1.75rem", fontWeight: 600, pt: 0.15 }}
                  >
                    {p.ordinal}.
                  </Typography>
                ) : null}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                      mb: 0.5,
                    }}
                  >
                    <Typography variant="subtitle2" component="span" fontWeight={700}>
                      {p.label}
                    </Typography>
                    {p.status ? (
                      <Chip
                        label={p.status}
                        size="small"
                        color={p.status === "PASS" ? "success" : "error"}
                        sx={{ height: 22, fontWeight: 600 }}
                      />
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      borderLeft: (theme) =>
                        `3px solid ${
                          p.status === "PASS"
                            ? theme.palette.success.main
                            : p.status === "FAIL"
                              ? theme.palette.error.main
                              : theme.palette.divider
                        }`,
                      pl: 1.25,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {p.body}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      {trailer ? (
        <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
          {trailerContainsUrl(trailer)}
        </Typography>
      ) : null}
    </Box>
  );
};

export default SubscriptionQcReasoning;
